import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon } from 'react-leaflet';
import L from 'leaflet';
import './MapPage.css';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Region boundaries (approximated for Scotland)
const regionBoundaries = {
  Highlands: [
    [57.8, -6.5], [58.5, -4.0], [57.5, -2.5], [56.5, -3.5], [56.0, -5.5], [57.8, -6.5]
  ],
  Islands: [
    [57.5, -7.5], [58.5, -6.0], [57.0, -5.0], [56.5, -6.5], [57.5, -7.5]
  ],
  Borders: [
    [55.0, -3.5], [55.8, -2.0], [55.0, -1.5], [54.5, -2.5], [55.0, -3.5]
  ],
  Cities: [
    [56.0, -4.0], [56.5, -3.0], [55.8, -3.0], [55.5, -4.0], [56.0, -4.0]
  ]
};

// Region colors
const regionColors = {
  Highlands: '#8b5cf6',    // Purple
  Islands: '#3b82f6',      // Blue
  Borders: '#10b981',      // Green
  Cities: '#f59e0b'        // Orange
};

// Activity colors for routes
const activityColors = {
  cycling: '#2d5016',      // Dark green
  hiking: '#d97706',       // Orange/brown
  roadtrip: '#1e40af'      // Blue
};

// Custom icons
const routeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const poiIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const selectedPOIIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [35, 57],
  iconAnchor: [17, 57],
  popupAnchor: [1, -50],
  shadowSize: [57, 57]
});


export default function MapPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const routeId = searchParams.get('routeId');
  const poiId = searchParams.get('poiId');
  
  const [routes, setRoutes] = useState([]);
  const [pois, setPois] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedPOI, setSelectedPOI] = useState(null);
  const [mapCenter, setMapCenter] = useState([56.8, -4.2]);
  const [loading, setLoading] = useState(true);
  const [showRoutes, setShowRoutes] = useState(true);
  const [showPOIs, setShowPOIs] = useState(true);
  const [showRegions, setShowRegions] = useState(true);
  const [activityFilter, setActivityFilter] = useState('all'); // all, cycling, hiking, roadtrip

  useEffect(() => {
    fetchData();
  }, []);

  // Re-fetch routes when activity filter changes
  useEffect(() => {
    if (activityFilter !== 'all') {
      fetchData();
    } else {
      fetchData(); // Also refetch for 'all' to get mixed routes
    }
  }, [activityFilter]);

  useEffect(() => {
    if (routeId && routes.length > 0) {
      const route = routes.find(r => r.id === routeId);
      if (route) {
        setSelectedRoute(route);
        setSelectedPOI(null);
        setShowRoutes(true);
        if (route.coordinates && route.coordinates.length > 0) {
          setMapCenter(route.coordinates[0]);
        }
      }
    }
  }, [routeId, routes]);

  useEffect(() => {
    if (poiId && pois.length > 0) {
      const poi = pois.find(p => p.id === poiId);
      if (poi) {
        setSelectedPOI(poi);
        setSelectedRoute(null);
        setShowPOIs(true);
        setMapCenter([poi.latitude, poi.longitude]);
      }
    }
  }, [poiId, pois]);

  const fetchData = async () => {
    try {
      // Build params for activity filter
      const params = {};
      if (activityFilter !== 'all') {
        params.activity_type = activityFilter;
      }
      
      const [routesRes, poisRes] = await Promise.all([
        axios.get(`${API}/routes`, { params }),
        axios.get(`${API}/pois`)
      ]);
      setRoutes(routesRes.data);
      setPois(poisRes.data);
    } catch (error) {
      console.error('Error fetching map data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container" data-testid="map-loading">
        <div className="loading-spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="map-page" data-testid="map-page">
      <div className="map-header">
        <div>
          <h1 className="page-title" data-testid="map-title">Interaktive Karte</h1>
          <p className="page-subtitle">Erkunde Routen und Sehenswürdigkeiten</p>
        </div>
        <div className="map-controls">
          <select 
            className="activity-filter"
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value)}
          >
            <option value="all">Alle Aktivitäten</option>
            <option value="cycling">🚴 Radfahren</option>
            <option value="hiking">🥾 Wandern</option>
            <option value="roadtrip">🚗 Roadtrip</option>
          </select>
          <button
            className={`control-btn ${showRegions ? 'active' : ''}`}
            onClick={() => setShowRegions(!showRegions)}
            data-testid="toggle-regions-btn"
          >
            Regionen {showRegions ? '✓' : ''}
          </button>
          <button
            className={`control-btn ${showRoutes ? 'active' : ''}`}
            onClick={() => setShowRoutes(!showRoutes)}
            data-testid="toggle-routes-btn"
          >
            Routen {showRoutes ? '✓' : ''}
          </button>
          <button
            className={`control-btn ${showPOIs ? 'active' : ''}`}
            onClick={() => setShowPOIs(!showPOIs)}
            data-testid="toggle-pois-btn"
          >
            POIs {showPOIs ? '✓' : ''}
          </button>
        </div>
      </div>

      {selectedRoute && (
        <div className="selected-route-banner">
          <div className="banner-content">
            <h3>{selectedRoute.name}</h3>
            <p>{selectedRoute.description}</p>
          </div>
          <button onClick={() => setSelectedRoute(null)} className="close-banner">×</button>
        </div>
      )}

      {selectedPOI && (
        <div className="selected-route-banner">
          <div className="banner-content">
            <h3>{selectedPOI.name}</h3>
            <p>{selectedPOI.description}</p>
          </div>
          <button onClick={() => setSelectedPOI(null)} className="close-banner">×</button>
        </div>
      )}

      <div className="map-container" data-testid="map-container">
        <MapContainer 
          key={`${mapCenter[0]}-${mapCenter[1]}-${selectedPOI?.id || selectedRoute?.id || 'default'}`}
          center={mapCenter} 
          zoom={selectedPOI || selectedRoute ? 12 : 7} 
          className="leaflet-map"
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Region Overlays */}
          {showRegions && Object.entries(regionBoundaries).map(([region, coordinates]) => (
            <Polygon
              key={region}
              positions={coordinates}
              pathOptions={{
                color: regionColors[region],
                fillColor: regionColors[region],
                fillOpacity: 0.15,
                weight: 2,
                opacity: 0.5
              }}
            >
              <Popup>
                <div className="map-popup">
                  <h3>{region}</h3>
                  <p>Region: {t(`regions.${region}`)}</p>
                </div>
              </Popup>
            </Polygon>
          ))}

          {/* Routes */}
          {showRoutes && routes.map((route) => {
            // No client-side filtering needed - backend does it now!
            const isSelected = selectedRoute && selectedRoute.id === route.id;
            const activityColor = activityColors[route.activity_type] || '#2563eb';
            
              return (
                <div key={route.id}>
                  {route.coordinates.length > 0 && (
                    <>
                      <Polyline 
                        positions={route.coordinates} 
                        color={isSelected ? '#ef4444' : activityColor}
                        weight={isSelected ? 5 : 3}
                        opacity={isSelected ? 1 : 0.7}
                      />
                      <Marker position={route.coordinates[0]} icon={routeIcon}>
                        <Popup>
                          <div className="map-popup">
                            <h3>{route.name}</h3>
                            <p className="popup-activity">
                              {route.activity_type === 'cycling' && '🚴 Radfahren'}
                              {route.activity_type === 'hiking' && '🥾 Wandern'}
                              {route.activity_type === 'roadtrip' && '🚗 Roadtrip'}
                            </p>
                            <p>{route.description}</p>
                            <div className="popup-stats">
                              <span>📏 {route.distance_km} km</span>
                              <span>⛰️ {route.elevation_gain_m} m</span>
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    </>
                  )}
                </div>
              );
            })}


          {/* POIs */}
          {showPOIs && pois.map((poi) => {
            const isSelected = selectedPOI && selectedPOI.id === poi.id;
            return (
              <Marker 
                key={poi.id} 
                position={[poi.latitude, poi.longitude]}
                icon={isSelected ? selectedPOIIcon : poiIcon}
              >
                <Popup>
                  <div className="map-popup">
                    <h3>{poi.name}</h3>
                    <p>{poi.description}</p>
                    {poi.address && <p className="popup-address">📍 {poi.address}</p>}
                    {poi.phone && <p className="popup-phone">📞 {poi.phone}</p>}
                    {poi.website && (
                      <p className="popup-website">
                        🌐 <a href={`https://${poi.website}`} target="_blank" rel="noopener noreferrer">{poi.website}</a>
                      </p>
                    )}
                    {poi.rating > 0 && <p>⭐ {poi.rating.toFixed(1)}</p>}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      <div className="map-legend" data-testid="map-legend">
        <h4>Legende</h4>
        <div className="legend-section">
          <h5>Aktivitäten:</h5>
          <div className="legend-item">
            <span className="legend-line" style={{ backgroundColor: activityColors.cycling }}></span>
            <span>🚴 Radfahren</span>
          </div>
          <div className="legend-item">
            <span className="legend-line" style={{ backgroundColor: activityColors.hiking }}></span>
            <span>🥾 Wandern</span>
          </div>
          <div className="legend-item">
            <span className="legend-line" style={{ backgroundColor: activityColors.roadtrip }}></span>
            <span>🚗 Roadtrip</span>
          </div>
        </div>
        <div className="legend-section">
          <h5>Regionen:</h5>
          {Object.entries(regionColors).map(([region, color]) => (
            <div key={region} className="legend-item">
              <span className="legend-region" style={{ backgroundColor: color }}></span>
              <span>{region}</span>
            </div>
          ))}
        </div>
        <div className="legend-section">
          <h5>Marker:</h5>
          <div className="legend-item">
            <span className="legend-marker blue"></span>
            <span>Routen ({routes.length})</span>
          </div>
          <div className="legend-item">
            <span className="legend-marker green"></span>
            <span>POIs ({pois.length})</span>
          </div>
        </div>
      </div>
    </div>
  );
}
